import * as THREE from "three";
import { buildIkChainFromScene } from "../chainBuilder";
import { computeKinematics } from "../forwardKinematics";
import { solvePositionIk } from "../solver";
import { getSceneActions, getSceneState } from "../../stores/sceneStore";
import { getIkTargetState } from "../../stores/ikTargetStore";
import { usePhysicsStore } from "../../stores/physicsStore";
import type { BodyUUID } from "../../types/scene";
import { logger } from "../../utils/logger";
import { getEndEffectorBody } from "./endEffector";
import { findGripperTargetOffset } from "./gripperTargetOffset";
import { getScalarJointRef } from "./jointUtils";
import { isFiniteVector3Array } from "./runtimeGuards";
import { stopActiveServoSession } from "./servoSession";
import { startServoToTargetObject } from "./pickPlaceServo";
import type { BodyRecord, IkDebugWindow, IkTargetPickedDetail } from "./types";

export function startIkTargetFollower() {
  const handler = (event: Event) => {
    const detail =
      event instanceof CustomEvent ? ((event.detail || {}) as IkTargetPickedDetail) : {};
    const eventTarget = isFiniteVector3Array(detail.world) ? detail.world : null;
    const markerWorld = getIkTargetState().markerWorld;
    const markerTarget = isFiniteVector3Array(markerWorld) ? markerWorld : null;
    const target = markerTarget || eventTarget;
    const targetSource = markerTarget ? "marker" : "event";
    if (!target || target.length < 3) return;

    // New request cancels the active servo session.
    stopActiveServoSession();

    const scene = getSceneState();
    const hintedEff = typeof detail.endEffectorUuid === "string" ? detail.endEffectorUuid : null;
    const endEffector = getEndEffectorBody(hintedEff);
    if (!endEffector) {
      logger.warn("[ik] No end-effector body available for IK target");
      return;
    }
    const bodies = (scene?.bodies || {}) as BodyRecord;
    const endEffectorName = bodies[endEffector]?.name || null;

    const chain = buildIkChainFromScene(scene, endEffector);
    if (!chain) {
      logger.warn("[ik] Failed to build IK chain for end-effector", endEffector);
      return;
    }

    // Seed IK with current joint values (store) or last successful solution if available
    const dofLinks = chain.links
      .filter(
        (l) => (l.kind === "hinge" || l.kind === "slide") && l.jointUuid && l.dofIndex !== undefined
      )
      .sort((a, b) => (a.dofIndex ?? 0) - (b.dofIndex ?? 0));
    const sceneSeed = dofLinks.map((link) => {
      const j = link.jointUuid ? scene.joints?.[link.jointUuid] : null;
      return getScalarJointRef(j);
    });
    const lastSolutionCandidate = (window as IkDebugWindow).__ikLastSolution;
    const lastSolution =
      Array.isArray(lastSolutionCandidate) && lastSolutionCandidate.length === chain.dofCount
        ? lastSolutionCandidate.map(Number)
        : null;
    const initial = lastSolution || sceneSeed;

    // Convert scene (Y-up) target into chain frame (MuJoCo/Z-up)
    const sceneTarget = new THREE.Vector3(target[0], target[1], target[2]);
    const isPhysicsRunning = usePhysicsStore.getState().isRunning === true;
    const minSceneY = 0.02;
    const yToZ = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI / 2, 0, 0));
    const zToY = yToZ.clone().invert();
    const zFrameTarget = sceneTarget.clone().applyQuaternion(yToZ);
    const useGripperTargetSite = detail.useGripperTargetSite === true;
    const targetOffset = useGripperTargetSite
      ? findGripperTargetOffset(scene, endEffector, yToZ, true)
      : null;
    const gripperForwardLocal =
      targetOffset?.forwardLocalZFrame instanceof THREE.Vector3
        ? targetOffset.forwardLocalZFrame.clone().normalize()
        : null;
    if (targetOffset) {
      chain.endEffectorOffset = [
        targetOffset.offsetZFrame.x,
        targetOffset.offsetZFrame.y,
        targetOffset.offsetZFrame.z,
      ];
    } else if (useGripperTargetSite) {
      logger.warn("[ik] Using end-effector origin; gripper_target site unavailable");
    }

    // In physics mode, if the user selected a target object, servo toward its live pose.
    // This is much more reliable than one-shot IK because the target (and the robot) can move under dynamics.
    const targetBodyUuid =
      typeof detail.bodyUuid === "string" ? (detail.bodyUuid as BodyUUID) : null;
    if (isPhysicsRunning && targetBodyUuid) {
      const dropoffBodyUuid =
        typeof detail.dropoffBodyUuid === "string" ? (detail.dropoffBodyUuid as BodyUUID) : null;
      startServoToTargetObject({
        targetBodyUuid,
        endEffectorUuid: endEffector as BodyUUID,
        useGripperTargetSite,
        dropoffBodyUuid,
      });
      return;
    }

    const floorExemptBodyUuids = new Set<BodyUUID>();
    Object.values(scene.bodies || {}).forEach((body) => {
      const name = (body?.name || "").toLowerCase();
      if (name.includes("pad")) {
        floorExemptBodyUuids.add(body.uuid as BodyUUID);
      }
    });

    const evaluatePath = (
      fromJoints: number[],
      toJoints: number[],
      targetScene: THREE.Vector3,
      approachMinY: number,
      approachTailSteps: number
    ): { safe: boolean; approachPenalty: number } => {
      const steps = 12;
      const allowEeBelowFloor = targetScene.y < minSceneY;
      let approachViolations = 0;
      for (let step = 0; step <= steps; step += 1) {
        const t = step / steps;
        const blended = toJoints.map((targetVal, idx) => {
          const startVal = fromJoints[idx] ?? 0;
          return startVal + (targetVal - startVal) * t;
        });
        const stepFk = computeKinematics(chain, blended);
        for (const [linkIdx, linkState] of stepFk.linkStates.entries()) {
          const linkScene = linkState.position.clone().applyQuaternion(zToY);
          if (step > 0 && linkScene.y < minSceneY) {
            const linkBodyUuid = chain.links[linkIdx]?.bodyUuid;
            const padAllowance = 0.01;
            const padMinY = minSceneY - padAllowance;
            if (!linkBodyUuid || !floorExemptBodyUuids.has(linkBodyUuid)) {
              return { safe: false, approachPenalty: 1 };
            }
            if (linkScene.y < padMinY) {
              return { safe: false, approachPenalty: 1 };
            }
          }
        }
        const eeSceneStep = stepFk.endEffector.position.clone().applyQuaternion(zToY);
        const isFinalApproach = step >= Math.max(steps - approachTailSteps, 0);
        if (!isFinalApproach && step > 0 && eeSceneStep.y < approachMinY) {
          approachViolations += 1;
        }
        if (step > 0 && eeSceneStep.y < minSceneY && !(allowEeBelowFloor && step === steps)) {
          return { safe: false, approachPenalty: 1 };
        }
      }
      const approachPenalty = steps > 0 ? approachViolations / steps : 0;
      return { safe: true, approachPenalty };
    };

    const candidateSceneTargets = isPhysicsRunning
      ? [
          sceneTarget.clone(),
          sceneTarget.clone().setY(sceneTarget.y + 0.05),
          sceneTarget.clone().setY(sceneTarget.y + 0.1),
        ]
      : [sceneTarget.clone()];
    const approachMinY = 0.035;
    const approachTailSteps = 3;

    let pick: ReturnType<typeof solvePositionIk> | null = null;
    let chosenTarget: THREE.Vector3 | null = null;
    let chosenAlignment: number | null = null;
    let chosenApproachPenalty: number | null = null;
    let chosenScore: number | null = null;
    let floorCollision = false;
    for (const candidate of candidateSceneTargets) {
      const candidateZ = candidate.clone().applyQuaternion(yToZ);
      const attempt = solvePositionIk(
        chain,
        { position: [candidateZ.x, candidateZ.y, candidateZ.z] },
        initial,
        {
          maxIterations: 192,
          tolerance: 1e-5,
          damping: 0.01,
          stepSize: 0.75,
        }
      );
      pick = attempt || null;
      if (!attempt || attempt.error > 0.05 || attempt.status !== "success") {
        continue;
      }
      const pathEval = evaluatePath(
        initial,
        attempt.jointValues,
        candidate,
        approachMinY,
        approachTailSteps
      );
      if (!pathEval.safe) {
        floorCollision = true;
        continue;
      }
      const approachPenaltyWeight = isPhysicsRunning ? 0.6 : 0;
      if (gripperForwardLocal) {
        const fkAttempt = computeKinematics(chain, attempt.jointValues);
        const eeSceneAttempt = fkAttempt.endEffector.position.clone().applyQuaternion(zToY);
        const approachDir = candidate.clone().sub(eeSceneAttempt);
        if (approachDir.lengthSq() > 1e-10) {
          approachDir.normalize();
          const gripperForwardScene = gripperForwardLocal
            .clone()
            .applyQuaternion(fkAttempt.endEffector.rotation)
            .applyQuaternion(zToY)
            .normalize();
          const alignment = gripperForwardScene.dot(approachDir);
          const score = alignment - pathEval.approachPenalty * approachPenaltyWeight;
          if (chosenScore === null || score > chosenScore) {
            pick = attempt;
            chosenTarget = candidate;
            chosenAlignment = alignment;
            chosenApproachPenalty = pathEval.approachPenalty;
            chosenScore = score;
            floorCollision = false;
          }
          continue;
        }
      }
      pick = attempt;
      chosenTarget = candidate;
      chosenApproachPenalty = pathEval.approachPenalty;
      floorCollision = false;
      break;
    }

    const solution = pick?.jointValues || initial;
    const fk = computeKinematics(chain, solution);
    const eeScene = fk.endEffector.position.clone().applyQuaternion(zToY);
    logger.debug("[IK] Solve summary", {
      frame: "zFrame",
      endEffector,
      endEffectorName,
      chainLinks: chain.links.length,
      joints: chain.links.map((l) =>
        l.jointUuid ? scene.joints?.[l.jointUuid]?.name || l.jointUuid : null
      ),
      targetSource,
      markerTarget,
      eventTarget,
      targetScene: { x: sceneTarget.x, y: sceneTarget.y, z: sceneTarget.z },
      targetZFrame: { x: zFrameTarget.x, y: zFrameTarget.y, z: zFrameTarget.z },
      chosenTarget: chosenTarget
        ? {
            x: chosenTarget.x,
            y: chosenTarget.y,
            z: chosenTarget.z,
          }
        : null,
      chosenAlignment,
      chosenApproachPenalty,
      gripperTarget: targetOffset
        ? {
            x: targetOffset.offsetZFrame.x,
            y: targetOffset.offsetZFrame.y,
            z: targetOffset.offsetZFrame.z,
            siteName: targetOffset.siteName,
            siteBodyUuid: targetOffset.siteBodyUuid,
          }
        : null,
      endEffectorScene: {
        x: eeScene.x,
        y: eeScene.y,
        z: eeScene.z,
      },
      iterations: pick?.iterations,
      error: pick?.error,
      status: pick?.status,
    });

    // Notify UI of IK result (panel can show a warning on failure).
    // Keep payload small and serializable.
    try {
      window.dispatchEvent(
        new CustomEvent("ik-solve-result", {
          detail: {
            status: pick?.status || "failed",
            error: pick?.error,
            iterations: pick?.iterations,
            endEffectorUuid: endEffector,
            endEffectorName,
          },
        })
      );
    } catch {
      // ignore
    }

    if (!pick || pick.error > 0.05 || pick.status !== "success") {
      logger.warn("[ik] IK solve did not converge — skipping joint update", pick);
      return;
    }
    if (floorCollision) {
      try {
        window.dispatchEvent(
          new CustomEvent("ik-solve-result", {
            detail: {
              status: "blocked",
              error: pick?.error,
              iterations: pick?.iterations,
              endEffectorUuid: endEffector,
              endEffectorName,
            },
          })
        );
      } catch {
        // ignore
      }
      return;
    }

    const sceneActions = getSceneActions();
    // Batch all joint updates into a single action to avoid intermediate states
    // This sends a single control message to the physics worker instead of one per joint
    const jointUpdates: Array<{ jointUuid: string; value: number }> = [];
    pick.jointValues.forEach((val, idx) => {
      const link = dofLinks.find((l) => l.dofIndex === idx) || null;
      const jointUuid = link?.jointUuid;
      if (jointUuid) jointUpdates.push({ jointUuid, value: val });
    });
    if (jointUpdates.length > 0) {
      sceneActions.updateJointValues(jointUpdates);
    }

    try {
      (window as IkDebugWindow).__ikLastSolution = pick.jointValues;
    } catch {
      // ignore
    }
  };

  window.addEventListener("ik-target-picked", handler);
  const clearHandler = () => stopActiveServoSession();
  window.addEventListener("ik-target-clear", clearHandler);
  return () => {
    window.removeEventListener("ik-target-picked", handler);
    window.removeEventListener("ik-target-clear", clearHandler);
    stopActiveServoSession();
  };
}
